import {
  BrushIcon,
  GlobeIcon,
  Goal,
  MessagesSquareIcon,
  Search,
  BookOpen,
} from "lucide-react";

// External apps icons
import CIPIcon from "@shared-assets/cip.png";
import ISearchIcon from "@shared-assets/isearch.png";
import AiSummitThumbnail from "@shared-assets/tutorial-videos/ai-summit-2025.jpg";
import CreateAiProfilesThumbnail from "@shared-assets/tutorial-videos/create-ai-profiles.jpg";
import WebSearchThumbnail from "@shared-assets/tutorial-videos/web-search.jpeg";
import DocumentGeneratorThumbnail from "@shared-assets/tutorial-videos/document-generator.jpeg";
import type { StaticImport } from "next/dist/shared/lib/get-img-props";
import type { FeatureFlagType } from "@shared-utils/constants";

import type { JSX } from "react";

type ExternalApp = {
  name: string;
  url: string;
  image: string | StaticImport;
};

export type Update = {
  date: string;
  key: string;
  title: string;
  url: string;
  body: string | JSX.Element;
};

export type TutorialVideo = {
  title: string;
  url: string;
  thumbnail: string | StaticImport;
};

type Metadata = {
  updates: Update[];
  aiTools: AITool[];
  tutorialVideos: TutorialVideo[];
  externalApps: ExternalApp[];
};

export type AITool = {
  slug:
    | "chat-ai"
    | "research-ai"
    | "training-ai"
    | "translate-ai"
    | "image-ai"
    | "isearch"
    | "docs"
    | "/";
  tool: boolean;
  featured?: boolean;
  requiredFeatureFlags?: FeatureFlagType[];
  meta: {
    fillColor?: string;
    strokeColor?: string;
    backgroundColor?: string;
    backgroundColorHover?: string;
    name: string;
    description: string;
    byline: string;
    contactName: string;
    contactEmail: string;
  };
};

export const getIconFromSlug = (
  slug: AITool["slug"],
  props?: {
    size?: number;
    strokeWidth?: number;
    className?: string;
  },
) => {
  switch (slug) {
    case "chat-ai":
      return <MessagesSquareIcon size={18} strokeWidth={0.75} {...props} />;
    case "translate-ai":
      return <GlobeIcon size={18} strokeWidth={0.75} {...props} />;
    case "image-ai":
      return <BrushIcon size={18} strokeWidth={0.75} {...props} />;
    case "training-ai":
      return <Goal size={18} strokeWidth={0.75} {...props} />;
    case "isearch":
      return <Search size={18} strokeWidth={0.75} {...props} />;
    case "research-ai":
      return <BookOpen size={18} strokeWidth={0.75} {...props} />;
  }
};

export const metadata: Metadata = {
  updates: [
    {
      date: "2026-06-02",
      key: "chat-history-compression",
      title: "Chat history compression available",
      url: "/updates#chat-history-compression",
      body: "LEO GPT now supports intelligent compression for conversations that exceed token limits. The system automatically summarizes older messages while preserving context, allowing you to continue seamlessly. When prompted, select 'Compress Chat' to archive previous messages and maintain conversation continuity. Additionally, you can export a complete markdown report of your conversation history for documentation purposes.",
    },
    {
      date: "2026-03-12",
      key: "brand-design-update",
      title: "The refreshed visual identity is now live on LEO AI.",
      url: "/updates",
      body: "We have updated the visual design of the app to better align with LEO's new brand guidelines. You will notice a refreshed look and feel across the experience, while the tools and workflows you use remain the same.",
    },
    {
      date: "2025-05-14",
      key: "agentic",
      title: "Agentic AI is here! ",
      url: "/updates",
      body: (
        <>
          {
            'The new version of Profiles allows you to upload multiple documents to a Profile. This opens the door for endless opportunities to train your own "AI agent" - for example, a Quality Assistant using the relevant EU regulation documents or a Legal Assistant trained on LEO contracts. See our '
          }
          <a
            className="text-ter-green-500 hover:underline"
            href="https://leopharma.sharepoint.com/teams/LEO-PHARMA-ACADEMY/SitePages/LEO-OpenAI---Search-Engine.aspx"
            target="_blank"
            rel="noreferrer"
          >
            {"LEO Academy video"}
          </a>
          {"on setting up a Profile for more details."}
        </>
      ),
    },
    {
      date: "2024-11-21",
      key: "plugins",
      title: "Plugin to ClinicalTrials.Gov available now!",
      url: "/updates",
      body: (
        <>
          {
            "You can now “chat” with ClinicalTrials.Gov content by simply selecting the “Clinical Trial Expert” profile in ChatGPT. You can also create your own Profile to best suit your needs – just click the box for the Plugin. "
          }
          <a
            className="text-ter-green-500 hover:underline"
            href="https://engage.cloud.microsoft/main/org/leo-pharma.com/threads/eyJfdHlwZSI6IlRocmVhZCIsImlkIjoiMzA3MjMzNjI5MTU5NDI0MCJ9?trk_copy_link=V2"
            target="_blank"
            rel="noreferrer"
          >
            {"Details in this message"}
          </a>
          .
        </>
      ),
    },
    {
      date: "2024-08-29",
      key: "ai-awards",
      title:
        "Call for entries to LEO AI Awards: all the details you need to know ",
      url: "/updates",
      body: (
        <>
          <p>
            Are you using LEO AI to boost the way you work and deliver business
            impact? You might be the winner we are looking for!{" "}
          </p>
          <p>
            All the details about the competition are included in this{" "}
            <a
              className="text-ter-green-500 hover:underline"
              target="_blank"
              rel="noreferrer"
              href="https://leopharma.sharepoint.com/sites/leopulse/News/Pages/Step-into-the-spotlight-with-the-launch-of-LEO-AI-awards.aspx"
            >
              Pulse article
            </a>
            .
          </p>
        </>
      ),
    },
    {
      date: "2024-08-09",
      key: "new-model",
      title: "Select GPT-4 for increased speed and precision.",
      url: "/updates",
      body: "We have enabled GPT-4 as a Profile option in LEO AI, which allows up to approx. 90,000 characters in a chat - that is the equivalent of 150-200 pages of text! Make sure you select this Profile to be able to input prompts or upload files of bigger sizes with improved speed and quality of the outputs.",
    },
    {
      date: "2024-04-24",
      key: "new-features",
      title: "Uploading your document onto LEO AI now possible.",
      url: "/updates",
      body: "An eagerly expected feature is now available on LEO AI: uploading your own document. \nUpload a file of up to 20 pages of text in “Chat GPT” if you want to: summarise, improve writing, extract key points, check for duplicates etc. \nUpload a file up to 40 MB to “Translate AI” if what you need it translated. Editable formats like Word or PowerPoint will work better, as you can edit the translated document. The algorithm can translate only text, so any images or charts included will stay in their original language. ",
    },
    {
      date: "2024-04-24",
      key: "darkmode",
      title: "Darkmode now available on LEO AI.",
      url: "/updates",
      body: "We are happy to announce that LEO AI now supports dark mode. You can now toggle between light and dark mode in the top right menu. We hope this feature will make your experience more enjoyable and easier on the eyes. If you have any feedback or suggestions, please let us know!",
    },
  ],
  aiTools: [
    {
      slug: "chat-ai",
      tool: true,
      featured: true,
      meta: {
        fillColor: "fill-ter-blue-500",
        strokeColor: "stroke-ter-blue-400",
        backgroundColor: "bg-ter-blue-500",
        backgroundColorHover: "hover:bg-ter-blue-200",
        name: "LEO GPT",
        description:
          "Ask questions, request help on specific tasks, get creative, or upload a document to “chat with”.",
        byline: "AI & Cloud Data Services",
        contactName: "Alex Schuleit",
        contactEmail: "alscu@leo-pharma.com",
      },
    },
    {
      slug: "isearch",
      requiredFeatureFlags: [],
      tool: true,
      meta: {
        fillColor: "fill-ter-cyan-500",
        strokeColor: "stroke-ter-cyan-500",
        backgroundColor: "bg-ter-cyan-500",
        backgroundColorHover: "hover:bg-ter-cyan-200",
        name: "iSearch",
        description:
          "An intelligent platform to help you manage & search complex databases.",
        byline: "AI & Cloud Data Services",
        contactName: "Alex Schuleit",
        contactEmail: "alscu@leo-pharma.com",
      },
    },
    {
      slug: "research-ai",
      tool: true,
      requiredFeatureFlags: ["RESEARCH_AI__APP__205402"],
      meta: {
        fillColor: "fill-ter-bluegray-400",
        strokeColor: "stroke-ter-bluegray-500",
        backgroundColor: "bg-ter-bluegray-500",
        backgroundColorHover: "hover:bg-ter-bluegray-200",
        name: "Research AI",
        description:
          "Conduct deep research tasks by scraping online information.",
        byline: "AI & Cloud Data Services",
        contactName: "Alex Schuleit",
        contactEmail: "alscu@leo-pharma.com",
      },
    },
    {
      slug: "training-ai",
      requiredFeatureFlags: ["TRAINING_AI__APP__189322"],
      tool: true,
      meta: {
        fillColor: "fill-ter-mint-500",
        strokeColor: "stroke-ter-mint-500",
        backgroundColor: "bg-ter-mint-500",
        backgroundColorHover: "hover:bg-ter-mint-200",
        name: "Role-Play AI",
        description:
          "Role-Play AI is a tool that helps you to train a machine learning model to predict the probability of a patient having a specific disease.",
        byline: "AI & Cloud Data Services",
        contactName: "Alex Schuleit",
        contactEmail: "alscu@leo-pharma.com",
      },
    },
    {
      slug: "translate-ai",
      tool: true,
      meta: {
        fillColor: "fill-ter-purple-500",
        strokeColor: "stroke-ter-purple-500",
        backgroundColor: "bg-ter-purple-500",
        backgroundColorHover: "hover:bg-ter-purple-200",
        name: "Translate AI",
        description:
          "Use the power of AI to translate short content or an entire document - while preserving its formatting. ",
        byline: "AI & Cloud Data Services",
        contactName: "Alex Schuleit",
        contactEmail: "alscu@leo-pharma.com",
      },
    },
    {
      slug: "image-ai",
      tool: true,
      meta: {
        fillColor: "fill-ter-green-500",
        strokeColor: "stroke-ter-green-500",
        backgroundColor: "bg-ter-green-500",
        backgroundColorHover: "hover:bg-ter-green-200",
        name: "Image GenAI",
        description: "Transform written prompts into powerful visuals. ",
        byline: "AI & Cloud Data Services",
        contactName: "Alex Schuleit",
        contactEmail: "alscu@leo-pharma.com",
      },
    },
    {
      slug: "isearch",
      tool: false,
      meta: {
        fillColor: "fill-ter-green-500",
        strokeColor: "stroke-ter-green-500",
        backgroundColor: "bg-ter-green-500",
        backgroundColorHover: "hover:bg-ter-green-400",
        name: "iSearch",
        description:
          "iSearch is an intelligent platform supported by machine learning algorithms to help you manage complex databases easier and faster. The platform is best suited for heavy text databases and tables with multiple text columns.",
        byline: "AI & Cloud Data Services",
        contactName: "Alex Schuleit",
        contactEmail: "alscu@leo-pharma.com",
      },
    },
    {
      slug: "docs",
      tool: false,
      meta: {
        name: "Docs",
        description: "Documentation for the AI & Cloud Data Services team",
        byline: "AI & Cloud Data Services",
        contactName: "Alex Schuleit",
        contactEmail: "alscu@leo-pharma.com",
      },
    },
  ],
  tutorialVideos: [
    {
      title: "AI Summit 2025",
      url: "https://leopharma.sharepoint.com/:v:/t/LEO-PHARMA-ACADEMY/IQAVWLXoBBVJRYgcejI1Bhr-AWnbRcAY3aCUvZo8sIoMfBE?e=MxeWDl&nav=eyJyZWZlcnJhbEluZm8iOnsicmVmZXJyYWxBcHAiOiJTdHJlYW1XZWJBcHAiLCJyZWZlcnJhbFZpZXciOiJTaGFyZURpYWxvZy1MaW5rIiwicmVmZXJyYWxBcHBQbGF0Zm9ybSI6IldlYiIsInJlZmVycmFsTW9kZSI6InZpZXcifX0%3D",
      thumbnail: AiSummitThumbnail,
    },
    {
      title: "Creating AI Profiles",
      url: "https://leopharma.sharepoint.com/:v:/s/ONELEOKOLuncertaintymatrix/IQAfkc0rU4VlS7lOn_owlP1wAUirM6kcOeExJAUUuXWpKQ8?e=5R2Xhd&nav=eyJyZWZlcnJhbEluZm8iOnsicmVmZXJyYWxBcHAiOiJTdHJlYW1XZWJBcHAiLCJyZWZlcnJhbFZpZXciOiJTaGFyZURpYWxvZy1MaW5rIiwicmVmZXJyYWxBcHBQbGF0Zm9ybSI6IldlYiIsInJlZmVycmFsTW9kZSI6InZpZXcifX0%3D",
      thumbnail: CreateAiProfilesThumbnail,
    },
    {
      title: "Web Search",
      url: "https://leopharma.sharepoint.com/:v:/s/ONELEOKOLuncertaintymatrix/IQAInln4Cs8LTIFGTN2dfPaJAQhwvLw7JU_aY7JNBclmUM4?e=I21Mx9&nav=eyJyZWZlcnJhbEluZm8iOnsicmVmZXJyYWxBcHAiOiJTdHJlYW1XZWJBcHAiLCJyZWZlcnJhbFZpZXciOiJTaGFyZURpYWxvZy1MaW5rIiwicmVmZXJyYWxBcHBQbGF0Zm9ybSI6IldlYiIsInJlZmVycmFsTW9kZSI6InZpZXcifX0%3D",
      thumbnail: WebSearchThumbnail,
    },
    {
      title: "Document generator",
      url: "https://leopharma.sharepoint.com/:v:/s/ONELEOKOLuncertaintymatrix/IQDNTosWbdGZQbbUarNk_27UAc7FRpKwNnfKiwGuDOvI_YA?e=fOfzjU&nav=eyJyZWZlcnJhbEluZm8iOnsicmVmZXJyYWxBcHAiOiJTdHJlYW1XZWJBcHAiLCJyZWZlcnJhbFZpZXciOiJTaGFyZURpYWxvZy1MaW5rIiwicmVmZXJyYWxBcHBQbGF0Zm9ybSI6IldlYiIsInJlZmVycmFsTW9kZSI6InZpZXcifX0%3D",
      thumbnail: DocumentGeneratorThumbnail,
    },
  ],
  externalApps: [
    {
      url: "https://isearch.leopharma-app.com/",
      name: "iSearch",
      image: ISearchIcon,
    },
    {
      url: "https://cip.leopharma-app.com/",
      name: "Competitor Information Platform",
      image: CIPIcon,
    },
  ],
};

export const slugToTool = metadata.aiTools.reduce((map, t) => {
  map.set(t.slug, t);
  return map;
}, new Map<string, AITool>());
